'use client';

import { forwardRef, useContext } from 'react';

import { css, cx } from '@/styled-system/css';

import { FormFieldContext } from '../form-field/context/form-field.context';
import { inputRecipe } from '../input/styles/input.styles';
import { textAreaRecipe } from './styles/textarea.styles';
import type { TextareaPropsInterface } from './types/textarea.types';

/**
 * A basic [HTML textarea element](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/textarea).
 *
 * @since 3.0.0
 */
export const Textarea = forwardRef<HTMLTextAreaElement, TextareaPropsInterface>(
  (props: TextareaPropsInterface, forwardedRef) => {
    const formFieldContext = useContext(FormFieldContext);

    const {
      css: cssProp,
      className,
      disabled = formFieldContext.disabled || false,
      error = formFieldContext.error || false,
      fluid = true,
      readOnly = formFieldContext.readOnly || false,
      required = formFieldContext.required || false,
      rows = 3,
      ...rest
    } = props;

    const styles = css(
      inputRecipe.raw({ disabled, error, fluid, readOnly, size: 'md' }),
      textAreaRecipe.raw({ readOnly }),
      cssProp,
    );

    return (
      <textarea
        aria-describedby={formFieldContext.descriptionId}
        aria-invalid={error}
        className={cx('c11n', 'c11n_textarea', styles, className)}
        data-c11n-component="Textarea"
        data-c11n-version={__C11N_VERSION__}
        disabled={disabled}
        id={formFieldContext.controlId}
        ref={forwardedRef}
        rows={rows}
        required={required}
        readOnly={readOnly}
        {...rest}
      />
    );
  },
);

Textarea.displayName = 'Textarea';
